import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  isSupportedLocale,
  resolveLocale,
  translate,
  type LocaleCode,
  type MessageCatalog,
} from '@event/i18n';
import { BOOTSTRAP_CATALOG } from './catalog';

const LOCALE_STORAGE_KEY = 'event.locale';

interface I18nContextValue {
  readonly locale: LocaleCode;
  readonly supportedLocales: readonly LocaleCode[];
  readonly catalog: MessageCatalog;
  readonly t: (
    key: string,
    values?: Readonly<Record<string, string | number>>,
  ) => string;
  readonly setLocale: (next: LocaleCode) => void;
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

function readInitialLocale(): LocaleCode {
  if (typeof window === 'undefined') {
    return DEFAULT_LOCALE;
  }
  const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
  if (isSupportedLocale(stored)) {
    return stored;
  }
  const navigatorLocale = window.navigator.language?.split('-')[0];
  return resolveLocale(navigatorLocale);
}

export function I18nProvider({ children }: { readonly children: ReactNode }) {
  const [locale, setLocaleState] = useState<LocaleCode>(() => readInitialLocale());

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: LocaleCode) => {
    if (!isSupportedLocale(next)) {
      return;
    }
    setLocaleState(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, next);
    }
  }, []);

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      supportedLocales: SUPPORTED_LOCALES,
      catalog: BOOTSTRAP_CATALOG,
      t: (key, values) =>
        translate(key, { locale, values, catalog: BOOTSTRAP_CATALOG }),
      setLocale,
    }),
    [locale, setLocale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const value = useContext(I18nContext);
  if (!value) {
    throw new Error('useI18n() must be used inside <I18nProvider>.');
  }
  return value;
}
